import { useEffect, useRef } from "react";
import { toaster } from "./chakra/toaster.tsx";

/**
 * Shows a one-time, persistent toast urging the user to enable SMS notifications
 * when their profile has contactByPhone = false. The toast:
 * - Appears bottom-right (configured in common/chakra/toaster.tsx)
 * - Does not auto-expire (duration: 0)
 * - Has a single action button that navigates to the Profile page
 * - Once the user clicks the action, we persist a localStorage flag to stop showing it again
 */
export function SmsOptInNudge({
  userInfo,
}: {
  userInfo?: { email?: string; contactByPhone?: boolean } | null;
}) {
  const toastIdRef = useRef<string | null>(null);
  const shownForEmailRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userInfo) return;

    const email = userInfo?.email || "unknown";
    const storageKey = `vip:sms-nudge-dismissed:${email}`;

    // If user opted in or previously dismissed, ensure any existing toast is closed and exit
    if (
      userInfo?.contactByPhone === true ||
      localStorage.getItem(storageKey) === "1"
    ) {
      if (toastIdRef.current != null) {
        toaster.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      shownForEmailRef.current = null;
      return;
    }

    // Avoid re-creating the toast repeatedly for the same email
    if (shownForEmailRef.current === email && toastIdRef.current != null)
      return;

    // Create a persistent toast with an action to open Profile
    const id = toaster.create({
      title: "Enable SMS notifications",
      description: "This way we can keep you up to date.",
      // Make it persistent until the user acts
      duration: 6000,
      // Don't show a generic close button; force explicit action so we can persist flag
      // closable: false,
      // type: "loading",
      // Provide an action button too, in case users prefer a button over the link
      action: {
        label: "Enable in Profile",
        onClick: () => {
          try {
            localStorage.setItem(storageKey, "1");
          } catch (err) {
            console.error(err);
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (window?.navigateTo) window.navigateTo("/profile");
          if (toastIdRef.current != null) toaster.dismiss(toastIdRef.current);
        },
      },
    });
    toastIdRef.current = id;
    shownForEmailRef.current = email;

    return () => {
      if (id != null) toaster.dismiss(id);
    };
    // Only re-run when user changes to avoid repeated toasts
  }, [userInfo?.email, userInfo?.contactByPhone]);

  return <></>;
}
